import { FormEvent, useEffect, useState } from 'react';
import { api, Learner, LeaderboardOptIn, Subject } from '../api';
import { Icon } from '../Icon';
import { useSession } from '../auth/session';
import { PreferencesPanel } from '../a11y/preferences';

/**
 * Everything about this account, in one place.
 *
 * These controls existed already but were scattered — the display name was at
 * the bottom of a statistics page, subjects were only settable at sign-up, the
 * password endpoint had no screen at all, and leaving was undocumented. A
 * person looking for "my details" should find them all together.
 *
 * Ordered by how often they are wanted and how hard they are to undo: identity
 * first, then security, then what you study, then preferences, and leaving last
 * behind a confirmation.
 */

const field = {
  width: '100%', maxWidth: '24rem', font: 'inherit', padding: '0.5rem',
  minHeight: '2.75rem', border: '1px solid var(--border-strong)', borderRadius: '8px',
  background: 'var(--surface)', color: 'var(--ink)',
} as const;

export function EditProfile({ onBack, onSignedOut }: {
  onBack: () => void; onSignedOut: () => void;
}) {
  const { learner, setLearner } = useSession();

  const [displayName, setDisplayName] = useState(learner?.displayName ?? '');
  const [email, setEmail] = useState(learner?.email ?? '');
  const [emailPassword, setEmailPassword] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [packIds, setPackIds] = useState<string[]>([]);
  const [boards, setBoards] = useState<LeaderboardOptIn>({ contributions: false, performance: false });

  const [confirmDelete, setConfirmDelete] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Subject[]>('/subjects').then(setSubjects).catch(() => {});
    api.get<{ packIds: string[] }>('/me/subjects').then((d) => setPackIds(d.packIds)).catch(() => {});
    api.get<LeaderboardOptIn>('/me/leaderboard').then(setBoards).catch(() => {});
  }, []);

  /** One place to run a save, so every section reports the same way. */
  async function run(key: string, fn: () => Promise<void>, ok: string) {
    setBusy(key); setMsg(null); setErr(null);
    try { await fn(); setMsg(ok); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(null); }
  }

  const emailChanged = (email ?? '').trim().toLowerCase() !== (learner?.email ?? '').toLowerCase();

  async function saveIdentity(e: FormEvent) {
    e.preventDefault();
    await run('identity', async () => {
      const updated = await api.put<Learner>('/me/profile', {
        displayName,
        email,
        ...(emailChanged ? { currentPassword: emailPassword } : {}),
      });
      setLearner(updated);
      setEmailPassword('');
    }, emailChanged ? 'Your details and email address were saved.' : 'Your details were saved.');
  }

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setErr('The two new passwords do not match.'); return; }
    await run('password', async () => {
      await api.put('/me/password', { currentPassword, newPassword });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    }, 'Password changed. Any other device signed into this account has been signed out.');
  }

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back</button>
      <h1>Your profile</h1>
      <p className="muted">Signed in as {learner?.email}.</p>

      {err && <div className="notice notice--warn" role="alert">{err}</div>}
      {msg && <div className="notice" role="status">{msg}</div>}

      {/* ------------------------------------------------------- identity */}
      <form onSubmit={saveIdentity} className="card">
        <h2 style={{ marginTop: 0 }}>Your details</h2>

        <label htmlFor="p-name">Display name</label>
        <input id="p-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
               style={field} aria-describedby="p-name-help" />
        <p id="p-name-help" className="muted" style={{ marginTop: '0.4rem' }}>
          This is the name shown on a leaderboard if you choose to take part.
        </p>

        <label htmlFor="p-email" style={{ marginTop: '1rem' }}>Email address</label>
        <input id="p-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
               style={field} autoComplete="email" aria-describedby="p-email-help" />
        <p id="p-email-help" className="muted" style={{ marginTop: '0.4rem' }}>
          This is what you sign in with, and where a password reset is sent.
        </p>

        {/* Shown only when it is actually needed, so the common case — renaming
            yourself — does not ask for a password for no reason. */}
        {emailChanged && (
          <div className="notice" style={{ marginTop: '0.75rem' }}>
            <p style={{ marginTop: 0 }}>
              <strong>Changing your email changes how you sign in.</strong> Confirm your current
              password so nobody else can move your account to an address you do not control.
            </p>
            <label htmlFor="p-email-pw">Current password</label>
            <input id="p-email-pw" type="password" value={emailPassword} autoComplete="current-password"
                   onChange={(e) => setEmailPassword(e.target.value)} style={field} />
          </div>
        )}

        <div className="btn-row" style={{ marginTop: '1rem' }}>
          <button className="btn-primary" type="submit" disabled={busy === 'identity'}>
            {busy === 'identity' ? 'Saving…' : 'Save details'}
          </button>
        </div>
      </form>

      {/* ------------------------------------------------------- password */}
      <form onSubmit={savePassword} className="card">
        <h2 style={{ marginTop: 0 }}>Password</h2>
        <label htmlFor="p-current">Current password</label>
        <input id="p-current" type="password" value={currentPassword} autoComplete="current-password"
               onChange={(e) => setCurrentPassword(e.target.value)} style={field} />

        <label htmlFor="p-new" style={{ marginTop: '1rem' }}>New password</label>
        <input id="p-new" type="password" value={newPassword} minLength={10} autoComplete="new-password"
               onChange={(e) => setNewPassword(e.target.value)} style={field}
               aria-describedby="p-new-help" />
        <p id="p-new-help" className="muted" style={{ marginTop: '0.4rem' }}>
          At least 10 characters. A few words you will remember beats a short scramble you will not.
        </p>

        <label htmlFor="p-confirm" style={{ marginTop: '1rem' }}>Repeat new password</label>
        <input id="p-confirm" type="password" value={confirmPassword} autoComplete="new-password"
               onChange={(e) => setConfirmPassword(e.target.value)} style={field} />

        <div className="btn-row" style={{ marginTop: '1rem' }}>
          <button type="submit"
                  disabled={busy === 'password' || !currentPassword || newPassword.length < 10}>
            {busy === 'password' ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>

      {/* ------------------------------------------------------- subjects */}
      <section className="card">
        <h2 style={{ marginTop: 0 }}>Subjects you study</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Your question banks and your progress are scoped to these. Choose at least one.
        </p>
        {subjects.map((s) => (
          <label key={s.id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            <input type="checkbox" checked={packIds.includes(s.id)}
                   onChange={(e) => setPackIds((cur) =>
                     e.target.checked ? [...cur, s.id] : cur.filter((x) => x !== s.id))} />
            <span>{s.subject} <span className="muted">({s.itemCount} questions)</span></span>
          </label>
        ))}
        <div className="btn-row" style={{ marginTop: '1rem' }}>
          <button disabled={busy === 'subjects' || packIds.length === 0}
                  onClick={() => run('subjects',
                    async () => { await api.put('/me/subjects', { packIds }); },
                    'Subjects saved.')}>
            {busy === 'subjects' ? 'Saving…' : 'Save subjects'}
          </button>
        </div>
      </section>

      {/* ---------------------------------------------------- leaderboards */}
      <section className="card">
        <h2 style={{ marginTop: 0 }}>Leaderboards</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Both are off unless you choose them, and each is separate. Only your display name is ever
          shown — never your email address.
        </p>
        <label className="choice" style={{ marginBottom: '0.5rem' }}>
          <input type="checkbox" checked={boards.contributions}
                 onChange={(e) => {
                   const next = { ...boards, contributions: e.target.checked };
                   setBoards(next);
                   void run('boards', async () => { await api.put('/me/leaderboard', next); },
                     'Leaderboard choices saved.');
                 }} />
          <span><strong>Contributions</strong><br />
            <span className="muted">Shows the cookies you have earned.</span></span>
        </label>
        <label className="choice">
          <input type="checkbox" checked={boards.performance}
                 onChange={(e) => {
                   const next = { ...boards, performance: e.target.checked };
                   setBoards(next);
                   void run('boards', async () => { await api.put('/me/leaderboard', next); },
                     'Leaderboard choices saved.');
                 }} />
          <span><strong>Practice performance</strong><br />
            <span className="muted">Makes how much you answer correctly visible to others.</span></span>
        </label>
      </section>

      {/* ----------------------------------------------------- preferences */}
      <section className="card">
        <h2 style={{ marginTop: 0 }}>Reading and display</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          These stay on this device and are never sent to the server.
        </p>
        <PreferencesPanel />
      </section>

      {/* ---------------------------------------------------------- leaving */}
      <section className="card">
        <h2 style={{ marginTop: 0 }}>Leaving</h2>
        <div className="btn-row">
          <button onClick={() => { void api.post('/auth/logout').finally(onSignedOut); }}>
            Sign out
          </button>
        </div>

        <div className="notice notice--warn" style={{ marginTop: '1rem' }}>
          <p style={{ marginTop: 0 }}>
            <strong>Delete your account.</strong> This cannot be undone. Your reports, contributed
            questions, cookies and subject choices are destroyed. Assessments you have taken are
            kept but detached from your name, so nothing identifies you.
          </p>
          <label htmlFor="p-delete">Type <code>{learner?.email}</code> to confirm</label>
          <input id="p-delete" value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)}
                 style={field} />
          <div className="btn-row" style={{ marginTop: '0.75rem' }}>
            <button disabled={confirmDelete !== learner?.email || busy === 'delete'}
                    onClick={() => run('delete', async () => {
                      await api.del('/me');
                      onSignedOut();
                    }, 'Account deleted.')}>
              Delete my account
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
